import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-custom-paginator',
  imports: [CommonModule],
  templateUrl: './custom-paginator.html',
  styleUrl: './custom-paginator.scss'
})
export class CustomPaginator implements OnChanges {
  @Input() currentPage: number = 1;
  @Input() totalRecords: number = 0;
  @Input() pageSize: number = 10;
  @Input() maxVisiblePages: number = 5;
  @Input() showFirstLast: boolean = true;
  @Input() showPrevNext: boolean = true;
  @Input() pageInfo: boolean = true;

  @Output() pageChange = new EventEmitter<number>();

  visiblePages: (number | string)[] = [];

  get totalPages(): number {
    return Math.ceil(this.totalRecords / this.pageSize) || 1;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentPage'] || changes['totalRecords'] || changes['pageSize'] || changes['maxVisiblePages']) {
      this.generateVisiblePages();
    }
  }

  generateVisiblePages(): void {
    this.visiblePages = [];
    
    if (this.totalPages <= this.maxVisiblePages) {
      for (let i = 1; i <= this.totalPages; i++) {
        this.visiblePages.push(i);
      }
      return;
    }

    const half = Math.floor(this.maxVisiblePages / 2);
    let start = Math.max(1, this.currentPage - half);
    let end = Math.min(this.totalPages, start + this.maxVisiblePages - 1);
    
    if (end === this.totalPages) {
      start = Math.max(1, end - this.maxVisiblePages + 1);
    }
    
    if (start > 1) {
      this.visiblePages.push(1);
      if (start > 2) this.visiblePages.push('...');
    }
    
    for (let i = start; i <= end; i++) {
      this.visiblePages.push(i);
    }
    
    if (end < this.totalPages) {
      if (end < this.totalPages - 1) this.visiblePages.push('...');
      this.visiblePages.push(this.totalPages);
    }
  }

  goToPage(page: number | string): void {
    if (typeof page === 'number' && page !== this.currentPage && page >= 1 && page <= this.totalPages) {
      this.pageChange.emit(page);
    }
  }

  goToFirst(): void {
    if (this.canGoFirst()) this.pageChange.emit(1);
  }

  goToLast(): void {
    if (this.canGoLast()) this.pageChange.emit(this.totalPages);
  }

  goToPrevious(): void {
    if (this.canGoPrevious()) this.pageChange.emit(this.currentPage - 1);
  }

  goToNext(): void {
    if (this.canGoNext()) this.pageChange.emit(this.currentPage + 1);
  }

  isNumber(value: number | string): boolean {
    return typeof value === 'number';
  }

  isEllipsis(value: number | string): boolean {
    return value === '...';
  }

  isCurrentPage(page: number | string): boolean {
    return typeof page === 'number' && page === this.currentPage;
  }

  canGoFirst(): boolean {
    return this.currentPage > 1;
  }

  canGoLast(): boolean {
    return this.currentPage < this.totalPages;
  }

  canGoPrevious(): boolean {
    return this.currentPage > 1;
  }

  canGoNext(): boolean {
    return this.currentPage < this.totalPages;
  }

  trackByPage(_index: number, page: number | string): number | string {
    return page;
  }
}
